import alertService from '@jetbrains/ring-ui/components/alert-service/alert-service'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import type {ListDataItem} from '@jetbrains/ring-ui/components/list/consts'
import {Type as ListItemType} from '@jetbrains/ring-ui/components/list/consts'
import PopupMenu from '@jetbrains/ring-ui/components/popup-menu/popup-menu'
import classNames from 'classnames'
import * as React from 'react'

import {copyClipboard} from '../../../../../utils/clipboard'
import {parseDateToObj} from '../../../../../utils/dateTimeUtils'
import {notNull} from '../../../../../utils/guards'
import {cmdOrCtrlPlus} from '../../../../../utils/keys'

import styles from './LogMessageMenu.css'

type Props = {
  id: number
  expanded: boolean
  expandable: boolean
  anchor: React.ReactElement
  text: string
  timestamp: string
  serverTimestamp?: string
  toggleMessageSubtree: (index: number, expand: boolean) => unknown
}

const DEFAULT_TIMEOUT = 2000

function LogMessageMenu({
  id,
  anchor,
  timestamp,
  text,
  serverTimestamp,
  expanded,
  expandable,
  toggleMessageSubtree,
}: Props) {
  const toggleSubtree = React.useCallback(() => {
    toggleMessageSubtree(id, !expanded)
  }, [id, expanded, toggleMessageSubtree])

  const menuItems: ListDataItem[] = React.useMemo(() => {
    const agentTimestampObj = serverTimestamp ? parseDateToObj(timestamp, false) : null
    const isSameTimezone = serverTimestamp
      ? agentTimestampObj?.utcOffset() === parseDateToObj(serverTimestamp, false).utcOffset()
      : true

    const mainItems = [
      expandable
        ? {
            template: (
              <span>
                {`${expanded ? 'Collapse' : 'Expand'} subtree`}
                <span className={styles.hotkey}>
                  {cmdOrCtrlPlus}
                  {expanded ? '\u2190' : '\u2192'}
                </span>
              </span>
            ),
            onClick: toggleSubtree,
            rgItemType: ListItemType.CUSTOM,
          }
        : null,
      navigator.clipboard != null
        ? [
            {
              label: 'Copy message',
              onClick: async () => {
                await copyClipboard(text)
                alertService.message('Message has been copied', DEFAULT_TIMEOUT)
              },
            },
            {
              label: 'Copy link to this message',
              onClick: async () => {
                await copyClipboard(location.href)
                alertService.message('Link has been copied', DEFAULT_TIMEOUT)
              },
            },
          ]
        : null,
    ]
      .flat()
      .filter(notNull) as ListDataItem[]
    const additionalItems: ListDataItem[] = [
      {
        className: styles.separator,
        rgItemType: ListItemType.SEPARATOR,
      },
      {
        template: <span>{`Agent time ${agentTimestampObj?.format('HH:mm:ss')}`}</span>,
        className: styles.timestamp,
        disabled: true,
        rgItemType: ListItemType.CUSTOM,
      },
    ]

    return isSameTimezone ? mainItems : mainItems.concat(additionalItems)
  }, [text, timestamp, serverTimestamp, expanded, expandable, toggleSubtree])

  return (
    <Dropdown anchor={anchor}>
      <PopupMenu
        className={classNames({[styles.popup]: menuItems.length > 0})}
        data={menuItems}
        closeOnSelect
      />
    </Dropdown>
  )
}

export default React.memo(LogMessageMenu)
